import moment from "moment";
import "moment/locale/pt-br";
import styled from "styled-components";
import { device } from "../../../constants";
import { useTranslation } from "next-i18next";
import { normalizePostContent } from "../../../utils/blog/normalizePostContent";

moment.locale("pt-br");

const Card = styled.a`
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-decoration: none;
  outline: none;

  
  :is(:focus, :active, :active:focus) span {
    color: #008c99 !important;
  }
  
  :is(:focus-within) {
    span {
      border: 1px solid #008c99;
      border-radius: 8px;
    }
  }
  
  @media ${device.laptop} {
    display: grid;
    grid-template-columns: 2fr 3fr;
    gap: 31px;

    :is(:hover, :focus) {
      img {
        transform: scale(1.02);
      }
    }
  }
 
  @media ${device.laptopL} {
    grid-template-columns: 482px 1fr;
  }

  :is([data-theme="dark"] &){
    background: transparent;
  }
`;

const PostCardImage = styled.img`
  border-radius: 8px;
  aspect-ratio: 2 / 1;
  object-fit: cover;
  max-width: 480px;
  transition: transform 0.3s ease-in-out;
  width: 342px;
  height: 160px;
  
  @media ${device.mobileL} {
    width: 100%;
    height: auto;
  }

  @media ${device.laptop} {
    aspect-ratio: 16 / 9;
    width: 100%;
    height: 100%;
  }
  
  @media ${device.laptopL} {
    width: 480px;
    height: 220px;
    aspect-ratio: auto;
    max-width: unset;
  }
`;

const PostCardTitle = styled.h1`
  color: ${({ theme }) => theme.colors["neutral-06"]};
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  margin-top: 16px;
  margin-bottom: 0;

  @media ${device.laptop} {
    font-size: 24px;
    margin: 0;
    margin-bottom: 16px;
    line-height: 32px;
  }

  :is([data-theme="dark"] &){
    color: ${({ theme }) => theme.colors["neutral-01"]};
  }
`;

const PostCardText = styled.div`
  margin-top: 16px;

  overflow: hidden;
  width: 100%;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;

  text-align: justify;
  position: relative;

  * {
    color: ${({ theme }) => theme.colors["neutral-05"]} !important;
  }

  :is([data-theme="dark"] &){
    color: ${({ theme }) => theme.colors["neutral-01"]} !important;

    * {
      color: ${({ theme }) => theme.colors["neutral-01"]} !important;
    }
  }

  & strong {
    font-weight: 600;
  }

  & p {
    color: ${({ theme }) => theme.colors["neutral-05"]} !important;
    font-size: 18px;
    margin-bottom: 0;
    font-weight: 400;
    text-align: justify;
    line-height: 30px;

    :is([data-theme="dark"] &){
      color: ${({ theme }) => theme.colors["neutral-01"]} !important;

      > * {
        color: ${({ theme }) => theme.colors["neutral-01"]} !important;
      }
    }
  }

  & > :is(h1, h2, h3, h4, h5, h6) {
    font-weight: 700;
    font-size: 20px;
    line-height: 100%;
    margin: 0;
  }

  @media ${device.laptop} {
    margin: 0;
    -webkit-line-clamp: 4;

    & p {
      font-size: 20px;
      line-height: 36px;
    }

    & > :is(h1, h2, h3, h4, h5, h6) {
      font-size: 24px;
    }
  }

  :is([data-theme="dark"] &){
    & p, & a, & span, & strong, & em, & li {
      color: ${({ theme }) => theme.colors["neutral-01"]} !important;
    }
  }
`;

const ReadMore = styled.span`
  text-transform: uppercase;
  text-decoration: underline;

  margin-right: auto;
  font-size: 18px;
  line-height: 30px;
  font-weight: 700;
  border: 1px solid transparent;
  
  &.read-more {
    background-color: ${({ theme }) => theme.colors["neutral-01"]};
    color: ${({ theme }) => theme.colors["branding-02"]}!important;
    
    :is([data-theme="dark"] &){
      background-color: ${({ theme }) => theme.colors["neutral-07"]};
      color: ${({ theme }) => theme.colors["vivid-02"]}!important;
    }
  }
`;

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;

  span,
  a {
    align-self: flex-end;
  }

  @media ${device.laptopL} {
    height: 100%;
    max-height: 220px;
  }
`;

export function PostCard({ data }) {
	const { t } = useTranslation();

	const slicedPost = normalizePostContent(
		data?.metadata.texto_preview_inicio || data?.metadata.texto_inicio,
	);

	return (
		<Card href={data?.slug}>
			<PostCardImage src={data?.metadata.imagem_inicio.url} />
			<CardBody>
				<PostCardTitle>{data?.title}</PostCardTitle>

				<PostCardText>
					<div
						dangerouslySetInnerHTML={{
							__html: slicedPost,
						}}
					/>
				</PostCardText>
				<ReadMore className="read-more">{t("blog.postReadMore")}</ReadMore>
			</CardBody>
		</Card>
	);
}
